import { FocusEventHandler, useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CellContext } from '@tanstack/react-table';

import { useNavigationContext } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { WebEDIAnnotation } from 'services/webediErrors/types';
import { useWebEDIStore } from 'stores/webedi/webedi';
import { cn } from 'support/helpers/cn/cn';
import { WebEDIRowData } from 'support/types/webediTable';
import { useValue } from 'components/WebEDI/helpers';

import { useFieldId } from '../hooks/useFieldId';
import { CellProps } from '../../types';
import { useCellClassNames, useEnumerableConfigurations } from '../cellHelpers';
import { InputInsideCell } from './InputInsideCell';

export type DropdownCellReadOnlyProps = CellContext<WebEDIRowData, unknown> &
  Omit<CellProps, 'register'> & {
    isNotShippedItemTransportUnit: boolean;
    isUnassignedItemTransportUnit: boolean;
  };

export const DropdownCellReadOnly = ({
  cell,
  column,
  row,
  table,
  isHighlighted,
  isNotShippedItemTransportUnit,
  isUnassignedItemTransportUnit,
  mode,
}: DropdownCellReadOnlyProps) => {
  const { t } = useTranslation();
  const isValidating = useWebEDIStore((state) => state.isValidating);
  const {
    columnDef: { meta: columnMeta },
    getIsFirstColumn,
    getIsLastColumn,
  } = column;

  const { id } = cell;

  const fieldId = useFieldId(id, column, table.options.meta?.dataPrefix, undefined, row, mode);

  const inputValue = useValue(row.original, fieldId, columnMeta?.optionalConfig?.expression);
  const { formattedValue } = useEnumerableConfigurations({
    meta: columnMeta,
    value: inputValue,
  });

  //We need to use formContext since we have a fake form in there so we get the right translations
  const { register } = useFormContext();
  const { onBlur, ...otherRegister } = register(fieldId);
  const { service, NavigableItemPopoverComponent, useNavigableItemPopover } = useNavigationContext<WebEDIAnnotation>();
  const { showNavigableItemPopover, setShowNavigableItemPopover, floatingStyles, refs } = useNavigableItemPopover();
  const error = service.useItemByFieldPath(fieldId);
  const setCurrentItem = service.setCurrentItem;

  const cellClassNames = useCellClassNames({
    isLastColumn: getIsLastColumn(),
    isFirstColumn: getIsFirstColumn(),
    row,
    table,
  });

  // An unassigned transport unit is a structural warning, not a validation annotation — it doesn't
  // go through the error/annotation system, so it's checked directly here rather than folded into
  // `error` below.
  const hasWarning = getHasWarning({ isUnassignedItemTransportUnit, hasError: Boolean(error) });

  const inputClassNames = cn(
    getInputStateClassNames({ hasError: Boolean(error), hasWarning }),
    cellClassNames,
    'text-start',
    columnMeta?.optionalConfig?.isStatusColumn ? 'pl-10!' : undefined,
  );

  useEffect(() => {
    if (showNavigableItemPopover && error) {
      setCurrentItem(error);
    }
  }, [showNavigableItemPopover, error, setCurrentItem]);

  const onBlurHandler: FocusEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      onBlur(e);
      setShowNavigableItemPopover(false);
    },
    [onBlur, setShowNavigableItemPopover],
  );

  const onFocusHandler = useCallback(() => {
    setShowNavigableItemPopover(true);
  }, [setShowNavigableItemPopover]);

  //We need the readOnly for the formatted value to be shown when rendering the type of the Parties
  //On number inputs we can't render formatted values because the input type is number and the formatted value will contain a currency symbol
  const valueToShow = isUnassignedItemTransportUnit
    ? t('webedi:line_items.field.transport_unit.unassigned')
    : isNotShippedItemTransportUnit
      ? t('webedi:line_items.field.transport_unit.not_shipped')
      : formattedValue || inputValue;

  return (
    <>
      <div
        data-cell-variant={hasWarning ? 'warning' : undefined}
        className={cn(
          'group flex size-full items-center justify-between gap-2',
          getWrapperStateClassNames({ hasError: Boolean(error), hasWarning, isHighlighted }),
          cellClassNames,
        )}
        ref={refs.setReference}
      >
        <InputInsideCell
          wrapperClassName="w-full"
          className={cn(
            'w-full rounded-none border-none! bg-transparent p-3 text-xs shadow-none! outline-hidden!',
            inputClassNames,
          )}
          placeholder={
            !formattedValue && error?.type === 'required' ? t('common:table.errors.requiredField') : undefined
          }
          title={formattedValue ? String(formattedValue) : undefined}
          {...otherRegister}
          value={valueToShow ?? ''}
          readOnly={true}
          type="text"
          fieldState={error ? 'error' : 'default'}
          onFocus={onFocusHandler}
          onBlur={onBlurHandler}
          id={fieldId}
          data-field={fieldId}
          data-testid={fieldId}
          data-1p-ignore
        />
      </div>
      {showNavigableItemPopover && NavigableItemPopoverComponent && (
        <NavigableItemPopoverComponent
          annotation={error}
          viewMode={false}
          ref={refs.setFloating}
          style={floatingStyles}
          isValidating={isValidating}
          fieldDescription={columnMeta?.description}
        />
      )}
    </>
  );
};

const getHasWarning = ({
  isUnassignedItemTransportUnit,
  hasError,
}: {
  isUnassignedItemTransportUnit: boolean;
  hasError: boolean;
}) => isUnassignedItemTransportUnit && !hasError;

const getInputStateClassNames = ({ hasError, hasWarning }: { hasError: boolean; hasWarning: boolean }) => {
  if (hasError) {
    return 'group-hover:bg-red-100 bg-red-50 focus:bg-transparent! disabled:bg-red-50!';
  }
  if (hasWarning) {
    return 'text-warning-700 group-hover:bg-warning-100 focus:bg-transparent! disabled:bg-warning-50!';
  }
  return 'group-hover:bg-procuros-green-50 focus:bg-transparent! disabled:bg-transparent!';
};

const getWrapperStateClassNames = ({
  hasError,
  hasWarning,
  isHighlighted,
}: {
  hasError: boolean;
  hasWarning: boolean;
  isHighlighted: boolean;
}) => {
  if (hasError) {
    return 'bg-red-50 focus-within:bg-transparent! focus-within:ring-red-500 focus-within:shadow-red-500 hover:bg-red-100';
  }
  if (hasWarning) {
    return 'bg-warning-50 text-warning-700 hover:bg-warning-100 focus-within:bg-transparent! focus-within:ring-warning-500 focus-within:shadow-warning-500';
  }
  return cn(
    'focus-within:bg-transparent! focus-within:shadow-procuros-green-500 focus-within:ring-procuros-green-500 hover:bg-procuros-green-50',
    { 'bg-amber-100': isHighlighted },
  );
};
